import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Drum, Play } from "lucide-react";
import { isFillMeasure } from "@/components/SheetMusic";

const OK_STEPS = [12, 16, 24];

export function collectFills(saved) {
  const out = [];
  saved.forEach((item) => {
    const t = item.tab || {};
    if (!/^4\//.test(t.time_signature || "4/4") || t.title === "Fill Library practice") return;
    const spm = t.steps_per_measure || 16;
    const usable = (sec, m) => m && OK_STEPS.includes(m.steps || spm) && !isFillMeasure(sec, m);
    let main = null;
    (t.sections || []).forEach((sec) => (sec.measures || []).forEach((m) => {
      if (!usable(sec, m)) return;
      if (!main || (!main.named && /groove|verse|riff|beat|chorus/i.test(sec.name || ""))) main = { m, named: /groove|verse|riff|beat|chorus/i.test(sec.name || "") };
    }));
    (t.sections || []).forEach((sec, si) => {
      const list = [...(sec.measures || []), ...(sec.ending || [])];
      list.forEach((m, mi) => {
        const steps = m.steps || spm;
        if (!OK_STEPS.includes(steps) || !isFillMeasure(sec, m)) return;
        const prev = mi > 0 ? list[mi - 1] : null;
        const g = usable(sec, prev) ? prev : main && main.m;
        out.push({
          key: `${item.id}.${si}.${mi}`, title: t.title, artist: t.artist, section: sec.name || "Fill",
          bpm: sec.bpm || t.bpm || 120, steps, lanes: m.lanes,
          groove: g ? { lanes: g.lanes, steps: g.steps || spm } : null,
        });
      });
    });
  });
  return out;
}

export const FillLibrary = ({ saved, onPractice }) => {
  const fills = useMemo(() => collectFills(saved), [saved]);
  const [picked, setPicked] = useState(() => new Set());
  const [pct, setPct] = useState(80);
  const [leadIn, setLeadIn] = useState(true);
  const all = picked.size === fills.length && fills.length > 0;

  const toggle = (k) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const practice = () => {
    const chosen = fills.filter((f) => picked.has(f.key));
    if (!chosen.length) return;
    const bpmOf = (f) => Math.max(30, Math.round((f.bpm * pct) / 100));
    onPractice({
      title: "Fill Library practice",
      artist: `${chosen.length} fill${chosen.length > 1 ? "s" : ""} · ${pct}% speed`,
      bpm: bpmOf(chosen[0]),
      time_signature: "4/4",
      steps_per_measure: 16,
      difficulty: "Mixed",
      _sheet: true,
      key: "—",
      notes: leadIn ? "Each groove bar → fill loops 4 times, then moves on to the next one." : "Each fill loops 4 times, then moves on to the next one.",
      sections: chosen.map((f) => ({
        name: `${f.title} – ${f.section}${/fill|solo|break/i.test(f.section) ? "" : " fill"}`,
        repeat: 4,
        bpm: bpmOf(f),
        measures: [...(leadIn && f.groove ? [{ ...f.groove, fill: false }] : []), { lanes: f.lanes, steps: f.steps, fill: true }].map(({ steps, ...m }) => (steps === 16 ? m : { ...m, steps })),
      })),
    });
  };

  if (!fills.length) {
    return (
      <div className="text-center py-12 text-slate-500" data-testid="fill-library-empty">
        <Drum className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No fills found yet. Save songs with fills to build your Fill Library.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="fill-library">
      <div className="rounded-xl border border-[#ffab00]/25 bg-[#ffab00]/[0.04] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-tab uppercase tracking-widest text-[#ffab00]">Speed</span>
          <Slider value={[pct]} min={60} max={100} step={10} onValueChange={(v) => setPct(v[0])} className="flex-1" data-testid="fill-speed-slider" />
          <span className="text-xs font-tab text-slate-300 w-10 text-right" data-testid="fill-speed-value">{pct}%</span>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <Switch checked={leadIn} onCheckedChange={setLeadIn} data-testid="fill-leadin-switch" /> Groove lead-in (play a groove bar before each fill)
        </label>
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setPicked(all ? new Set() : new Set(fills.map((f) => f.key)))} className="text-xs text-[#00f0ff] hover:underline" data-testid="fill-select-all">
            {all ? "Clear all" : `Select all (${fills.length})`}
          </button>
          <Button size="sm" onClick={practice} disabled={!picked.size} className="bg-[#ffab00] text-black hover:bg-[#ffc233] h-8" data-testid="fill-practice-all">
            <Play className="w-3.5 h-3.5 mr-1" /> Practice all ({picked.size})
          </Button>
        </div>
        <span className="text-[11px] text-slate-500">{leadIn ? "Groove bar → fill" : "Each fill"} loops 4 times at its song's tempo × your speed, with sticking shown.</span>
      </div>
      {fills.map((f) => (
        <label key={f.key} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${picked.has(f.key) ? "border-[#ffab00]/50 bg-[#ffab00]/[0.05]" : "border-white/10 bg-white/[0.03] hover:border-[#00f0ff]/30"}`} data-testid={`fill-item-${f.key}`}>
          <Checkbox checked={picked.has(f.key)} onCheckedChange={() => toggle(f.key)} data-testid={`fill-check-${f.key}`} />
          <div className="min-w-0 flex-1">
            <p className="font-semibold truncate text-sm">{f.title}</p>
            <p className="text-xs text-slate-500 truncate">{f.artist} · {f.section}</p>
          </div>
          <span className="text-[10px] font-tab text-[#00f0ff] shrink-0">{f.bpm} BPM{f.steps !== 16 ? " · triplets" : ""}</span>
        </label>
      ))}
    </div>
  );
};
